/** Strip a trailing diagnosis code such as " - d50.9" from a source phrase. */
const stripDiagnosisCode = (source: string): string =>
  source.replace(/\s*[-–]\s*[A-Za-z]?\d[\w.]*\s*$/i, "").trim();

/** Return the first recorded source phrase with its diagnosis code removed. */
export const getPrimarySource = (
  sourceText: Record<string, number> | undefined | null,
): string => {
  if (!sourceText) return "";
  const [first] = Object.keys(sourceText);
  return first ? stripDiagnosisCode(first) : "";
};

/** A suggested source phrase with its share of the lane's recorded sources. */
export interface SourceSuggestion {
  source: string;
  /** Percent of recorded source mentions (0-100, rounded). */
  percent: number;
}

/** Return cleaned, de-duplicated source phrases (with recorded order) and the
 * percent of recorded source mentions each one accounts for. */
export const getSourceSuggestions = (
  sourceText: Record<string, number> | undefined | null,
  limit = 2,
): SourceSuggestion[] => {
  if (!sourceText) return [];

  const counts = new Map<string, number>();
  for (const [source, count] of Object.entries(sourceText)) {
    const cleaned = stripDiagnosisCode(source);
    if (cleaned) counts.set(cleaned, (counts.get(cleaned) ?? 0) + count);
  }
  const total = [...counts.values()].reduce((sum, n) => sum + n, 0);

  const suggestions: SourceSuggestion[] = [];
  for (const [source, count] of counts) {
    suggestions.push({
      source,
      percent: total ? Math.round((count / total) * 100) : 0,
    });
    if (suggestions.length >= limit) break;
  }
  return suggestions;
};
